import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Command, CommandList } from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchInput } from '@/components/ui/search-input';
import { useState } from 'react';
import { FilterLists, type TabBarStates } from './filter-lists';
import { FilteredItems } from './filtered-items';
import { TabBar } from './tab-bar';

import { AnimatePresence } from 'framer-motion';
import { useDebounceCallback } from 'usehooks-ts';
import { useApplyGlobalFilter } from '../hooks/use-apply-global-filter';
import { useFilteredItems } from '../hooks/use-filtered-items';
import { type GlobalFilterItem } from '../types';

export function GlobalFilterModal({
  close,
  cachedFilters,
}: {
  close: () => void;
  cachedFilters: GlobalFilterItem[];
}) {
  const applyGlobalFilter = useApplyGlobalFilter();
  const [searchValue, setSearchValue] = useState('');
  const [selectedTab, setSelectedTab] = useState<TabBarStates>('segments');
  const { filteredItems, setFilteredItems, toggleFilteredItem } =
    useFilteredItems(cachedFilters);
  const debouncedSearch = useDebounceCallback(setSearchValue, 300);

  const onSave = () => {
    applyGlobalFilter(filteredItems);
    close();
  };

  return (
    <Dialog modal open onOpenChange={close}>
      <DialogContent className='flex min-w-200 flex-col gap-0 p-0'>
        <DialogHeader>
          <DialogTitle>Filtrera i systemet</DialogTitle>
          <Button variant='outline' size='icon-sm' onClick={close}>
            <Icons.close className='size-4' />
          </Button>
          <DialogDescription className='sr-only'>
            Filtrera systemet på bolag, fastighet, segment och uppdragsgivare.
          </DialogDescription>
        </DialogHeader>
        <div className='flex items-center justify-between gap-3 border-b px-4 py-2'>
          <SearchInput
            containerClassName='flex-1'
            onChange={(e) => debouncedSearch(e.target.value)}
            placeholder='Sök...'
          />
          <TabBar selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
        </div>

        <AnimatePresence>
          {!!filteredItems.length && (
            <FilteredItems
              items={filteredItems}
              toggleFilteredItem={toggleFilteredItem}
              clearAll={() => {
                setFilteredItems([]);
              }}
            />
          )}
        </AnimatePresence>

        {/* The rows are cmdk CommandItems (shared with the ⌘K palette's
            filter page), so they need a Command ancestor even though the
            modal's own input isn't a cmdk input. */}
        <Command
          shouldFilter={false}
          className='h-auto rounded-none bg-transparent'
        >
          <CommandList className='relative h-112.5 max-h-none overflow-y-auto'>
            <FilterLists
              selectedTab={selectedTab}
              search={searchValue}
              filteredItems={filteredItems}
              toggleFilteredItem={toggleFilteredItem}
            />
          </CommandList>
        </Command>

        <DialogFooter>
          <Button onClick={onSave} size='sm'>
            Spara
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
